/* ============================================================================
   The Science: the receptor mechanism.

     <unc-sc-mech>  three stages of adenosine-receptor occupancy

   Loaded only on the page whose template suffix is `sustainability`.

   SELECTION LIVES IN THE DOM AND NOWHERE ELSE, the same as every other
   interactive element in this theme. `aria-selected` on the step buttons and
   `data-step` on the host drive every highlight through CSS attribute
   selectors, so there is no parallel state object to fall out of sync.

   ALL THREE STAGES ARE SERVER-RENDERED, ring and counts included. Switching
   stage only changes which group is visible and what the readout says, so
   nothing is built in the browser and the receptor counts can be derived from
   the fill table in Liquid rather than recomputed here. With JS blocked the
   page shows stage one, correct and readable, just not advancing.
   ============================================================================ */
(function () {
  'use strict';

  /* Shared pause-aware interval from unc-pdp.js: pauses on hover, on
     focus-within, off-screen, and on a hidden tab. Exported there rather than
     reimplemented, so there is one set of pause rules for the whole site. */
  function autoCycle(host, step, ms) {
    if (typeof window.uncAutoCycle === 'function') return window.uncAutoCycle(host, step, ms);
    var id = setInterval(step, ms);
    return function () {
      clearInterval(id);
    };
  }

  function text(el, value) {
    if (el) el.textContent = value;
  }

  /* --------------------------------------------------------------------------
     THE HERO'S PROGRESSIVE DARKENING.

     Writes --unc-sc-dim, 0 to 1, from how far the stage has scrolled past the
     top of the viewport. The CSS multiplies that by 0.82 for the final opacity,
     so the footage fades out under the mechanism card as the card rides over it.

     Not gated on prefers-reduced-motion. Nothing moves: this is a colour change
     tied to scroll position, which carries none of the vestibular risk that the
     setting exists to remove, and gating it would leave the hero at full
     brightness behind a card that has already covered it.

     rAF-throttled, and the listener is passive, so it cannot delay scrolling. */
  (function heroDim() {
    var stage = document.querySelector('.unc-sc-herostage');
    if (!stage) return;
    var hero = stage.querySelector('.unc-sc-hero');
    if (!hero) return;

    var queued = false;

    function paint() {
      queued = false;
      var glide = stage.offsetHeight - hero.offsetHeight;
      if (glide <= 0) {
        hero.style.setProperty('--unc-sc-dim', '0');
        return;
      }
      var gone = -stage.getBoundingClientRect().top;
      var p = Math.min(Math.max(gone / glide, 0), 1);
      hero.style.setProperty('--unc-sc-dim', p.toFixed(3));
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    paint();
  })();

  if (!customElements.get('unc-sc-mech')) {
    customElements.define(
      'unc-sc-mech',
      class UncScMech extends HTMLElement {
        connectedCallback() {
          this.steps = Array.from(this.querySelectorAll('[data-sc-step]'));
          this.groups = Array.from(this.querySelectorAll('[data-sc-group]'));
          if (this.steps.length < 2 || !this.groups.length) return;

          this.outState = this.querySelector('[data-sc-outstate]');
          this.outSub = this.querySelector('[data-sc-outsub]');
          this.outPhase = this.querySelector('[data-sc-outphase]');
          this.outHead = this.querySelector('[data-sc-outhead]');
          this.outBody = this.querySelector('[data-sc-outbody]');
          this.outFeel = this.querySelector('[data-sc-outfeel]');
          this.outCount = this.querySelector('[data-sc-outcount]');

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);

          var open = this.steps.filter(function (s) {
            return s.getAttribute('aria-selected') === 'true';
          })[0];
          this.select(open || this.steps[0]);

          /* The comp keeps cycling but HOLDS for 12 seconds after a click, then
             picks up again. It does not stop for good, unlike the Founder's
             Story loop, so the hold is a timestamp check inside the step rather
             than clearing the interval: someone who taps a stage to read it
             gets to finish, and the diagram still demonstrates itself to
             everyone who never touches it. */
          var every = parseInt(this.dataset.interval || '5200', 10);
          this.hold = parseInt(this.dataset.hold || '12000', 10);
          this.heldAt = 0;

          if (every > 0) {
            var self = this;
            this.stopAuto = autoCycle(
              this,
              function () {
                if (Date.now() - self.heldAt < self.hold) return;
                var i = self.steps.indexOf(self.current());
                self.select(self.steps[(i + 1) % self.steps.length]);
              },
              every
            );
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
          if (this.stopAuto) this.stopAuto();
        }

        current() {
          return (
            this.steps.filter(function (s) {
              return s.getAttribute('aria-selected') === 'true';
            })[0] || this.steps[0]
          );
        }

        onClick(e) {
          var step = e.target.closest('[data-sc-step]');
          if (!step || !this.contains(step)) return;
          this.heldAt = Date.now();
          this.select(step);
        }

        onKeydown(e) {
          var step = e.target.closest('[data-sc-step]');
          if (!step || !this.contains(step)) return;
          var i = this.steps.indexOf(step);
          var last = this.steps.length - 1;
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          else return;
          e.preventDefault();
          this.heldAt = Date.now();
          this.select(this.steps[next]);
          this.steps[next].focus();
        }

        select(step) {
          if (!step) return;
          var n = step.dataset.scStep;
          this.dataset.step = n;

          this.steps.forEach(function (s) {
            var on = s === step;
            s.setAttribute('aria-selected', on ? 'true' : 'false');
            s.setAttribute('tabindex', on ? '0' : '-1');
          });

          /* `hidden` rather than a class, so the sockets of the two stages that
             are not showing are out of the accessibility tree entirely. They
             sit stacked on the same ring and would otherwise be read out three
             times over. */
          this.groups.forEach(function (g) {
            g.hidden = g.dataset.scGroup !== n;
          });

          text(this.outState, step.dataset.scState || '');
          text(this.outSub, step.dataset.scSub || '');
          text(this.outPhase, step.dataset.scPhase || '');
          text(this.outHead, step.dataset.scHead || '');
          text(this.outBody, step.dataset.scBody || '');
          text(this.outFeel, step.dataset.scFeel || '');
          text(this.outCount, step.dataset.scCount || '');
        }
      }
    );
  }
})();
